import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import router          from '@/router'
import { useAppStore } from '@/stores/app'

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: '/api/tpm/v1',
        options: {
            // §6's error envelope reaches the caller. VueUse clears `data` on any non-2xx before
            // it hands the response back, so without this every `parseError(data.value, …)` in the
            // panel sees `null` and prints its generic fallback — the envelope's integer `code`,
            // which is the SPA's whole localization contract for server errors (§9.5), would be
            // parsed by nobody. Success is still decided by the status, never by `data` alone, so
            // an error body can never be mistaken for a document.
            updateDataOnError: true,

            beforeFetch({ options }) {
                // No `Authorization` header, no token, nothing of ours on the request (D76,
                // common contract §3). The session is the opaque `__Host-tpm_session` cookie the
                // server set `HttpOnly`; every call is same-origin, so the browser attaches it
                // for us and nothing here could read it anyway.
                //
                // `same-origin` and not `include`: the panel makes no credentialed cross-origin
                // request, and the server's CSRF check (common contract §5) would refuse one.
                // That check reads the `Origin` and `Sec-Fetch-Site` headers the browser adds
                // to every same-origin `fetch` mutation by itself — so this hook must never set a
                // request `mode`, delete a header, or otherwise touch that evidence. It sets the
                // one field and returns.
                options.credentials = 'same-origin'
                return { options }
            },
            onFetchError(ctx) {
                // §9.1 — ANY 401 ends the session and goes back to /login, remembering where we
                // were. Nothing is parsed to decide that: the SPA never sees the cookie or a
                // token, and a session ends **server-side**, at any moment, for reasons the SPA
                // cannot predict — a local logout on another tab, a `logout-everywhere` or a
                // disable that bumped the user's `auth_epoch`, a back-channel logout from the
                // IdP, a terminal (`invalid_grant`/`access_denied`) or ambiguous refresh, the
                // 12 h absolute expiry. The 401 is the one signal, and it is acted on exactly
                // once: clear the non-secret state, navigate to the local sign-in page, and stop
                // — the page there waits for its button and never restarts OIDC by itself.
                // 403 is deliberately NOT handled here — it means "signed in, no access", the
                // no-access view or a toast is the answer, and redirecting on it produces a
                // re-login loop.
                if (ctx.response?.status === 401) {
                    // Any 401 that lands while the boot `/auth/me` is still in flight — its own,
                    // or one from a request that raced it — is the guard's to act on, not ours.
                    // The guard is parked on `sessionReady`, so a `router.push` from here waits on
                    // a promise that waits on this very fetch, and the navigation never completes.
                    //
                    // `isSessionSettled()` and NOT `session.ready`: `clearSession()` on the line
                    // below sets that one, so the first racing 401 would otherwise tell the second
                    // that the bootstrap had finished when it had not. That was the login race.
                    const settled = appStore.isSessionSettled()
                    appStore.clearSession()

                    const current = router.currentRoute.value
                    if (settled && current.name !== 'login') {
                        // Not awaited, and this handler is not async: a fetch must never block on
                        // a navigation, because a guard is free to await something — a lazy chunk,
                        // the session — that is downstream of the request being handled here.
                        void router.push({ name: 'login', query: { redirect: current.fullPath } })
                            .catch(() => { /* a superseded navigation is not an error worth raising */ })
                    }
                }
                return ctx
            },
        },
    })

    return { fetch }
})
